import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface WsEvent {
  id: string
  event: string
  instanceId: string
  data: unknown
  timestamp: string
}

const MAX_EVENTS = 500

export const useWsStore = defineStore('ws', () => {
  const events = ref<WsEvent[]>([])
  const connected = ref(false)
  const paused = ref(false)

  let ws: WebSocket | null = null
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let reconnectDelay = 1000
  let currentToken = ''
  let currentInstanceId = ''

  function connect(token: string, instanceId = '') {
    currentToken = token
    currentInstanceId = instanceId
    _open()
  }

  function _open() {
    if (ws) { ws.onclose = null; ws.close() }
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:'
    let url = `${protocol}//${location.host}/ws?token=${currentToken}`
    if (currentInstanceId) url += `&instanceId=${currentInstanceId}`

    ws = new WebSocket(url)

    ws.onopen = () => {
      connected.value = true
      reconnectDelay = 1000
    }

    ws.onclose = () => {
      connected.value = false
      // Exponential backoff reconnect
      reconnectTimer = setTimeout(() => {
        reconnectDelay = Math.min(reconnectDelay * 2, 30000)
        _open()
      }, reconnectDelay)
    }

    ws.onerror = () => {
      ws?.close()
    }

    ws.onmessage = (e) => {
      if (paused.value) return
      try {
        const data = JSON.parse(e.data)
        const entry: WsEvent = {
          id: crypto.randomUUID(),
          event: data.event ?? 'UNKNOWN',
          instanceId: data.instanceId ?? '',
          data: data.data ?? data,
          timestamp: new Date().toISOString(),
        }
        events.value.unshift(entry)
        if (events.value.length > MAX_EVENTS) events.value.length = MAX_EVENTS
      } catch {
        // ignore non-JSON
      }
    }
  }

  function disconnect() {
    if (reconnectTimer) clearTimeout(reconnectTimer)
    if (ws) { ws.onclose = null; ws.close(); ws = null }
    connected.value = false
  }

  function clear() {
    events.value = []
  }

  return { events, connected, paused, connect, disconnect, clear }
})
